import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";
import { supabaseStorageCompat as supabase } from "./fileStorage";
import { db } from "./dbClient";

const DEFAULT_THEME = {
  text: "#fff", textDim: "#999", textMid: "#bbb", border: "#333", borderLight: "#444",
  surface: "#1a1a1a", surfaceAlt: "#222", bg: "#0d0d0d", accent: "#00d4ff",
  accentGlow: "rgba(0,212,255,0.1)", warn: "#ff9800", danger: "#ff4444", success: "#4caf50", purple: "#9c27b0"
};

export default function CustomersPage({ currentUser, company }) {
  const C = useTheme() || DEFAULT_THEME;
  const { t } = useTranslation();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [changeReason, setChangeReason] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Compliance dashboard state
  const [selectedTab, setSelectedTab] = useState('profile');
  const [alerts, setAlerts] = useState([]);
  const [sars, setSars] = useState([]);
  const [ctrs, setCtrs] = useState([]);
  const [eddReviews, setEddReviews] = useState([]);
  const [eddCustomer, setEddCustomer] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [customerDocuments, setCustomerDocuments] = useState([]);
  const [kycAnalysis, setKycAnalysis] = useState(null);

  // Load approved customers via backend API
  useEffect(() => {
    if (!company?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('amlguard_token');
    fetch(`/api/query?resource=kyc_submissions&filter={"company_id":"${company.id}","status":"approved"}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    })
      .then(res => res.json())
      .then(data => {
        setCustomers(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error("[CustomersPage] API error", err);
        setCustomers([]);
      })
      .finally(() => setLoading(false));
  }, [company?.id]);

  // Load transactions for selected customer
  useEffect(() => {
    if (!selectedCustomer || !company?.id) {
      setTransactions([]);
      return;
    }

    setTransactionsLoading(true);
    const customerName = getName(selectedCustomer);
    const firstNames = customerName.split(' ').map(n => n.toLowerCase());
    const token = localStorage.getItem('amlguard_token');

    // Fetch all transactions for the company
    fetch(`/api/query?resource=transactions&filter={"company_id":"${company.id}"}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    })
      .then(res => res.json())
      .then(data => {
        // Filter transactions by customer name across all possible fields
        const filtered = Array.isArray(data)
          ? data.filter(tx => {
              const desc = (tx.description || "").toLowerCase();
              const entity = (tx.entity || "").toLowerCase();
              const toLabel = (tx.toLabel || "").toLowerCase();
              const fromLabel = (tx.fromLabel || "").toLowerCase();

              // Check if customer name appears in any field
              const customerLower = customerName.toLowerCase();
              const fullMatch = desc.includes(customerLower)
                || entity.includes(customerLower)
                || toLabel.includes(customerLower)
                || fromLabel.includes(customerLower)
                || entity === customerLower
                || toLabel === customerLower
                || fromLabel === customerLower;

              // Also check if any part of the name matches (for partial matches)
              const partialMatch = firstNames.some(name =>
                (desc.includes(name) && name.length > 3) ||
                entity.includes(name) ||
                toLabel.includes(name) ||
                fromLabel.includes(name)
              );

              return fullMatch || partialMatch;
            })
          : [];

        // Sort by date, newest first
        filtered.sort((a, b) => {
          const dateA = new Date(a.date || a.created_at || 0);
          const dateB = new Date(b.date || b.created_at || 0);
          return dateB - dateA;
        });

        setTransactions(filtered);
      })
      .catch(err => {
        console.error("[CustomersPage] Transactions fetch error", err);
        setTransactions([]);
      })
      .finally(() => setTransactionsLoading(false));
  }, [selectedCustomer, company?.id]);

  // Reset tab and load documents/analysis when modal closes/opens
  useEffect(() => {
    if (selectedCustomer) {
      setSelectedTab('profile');
      const analysis = selectedCustomer.data_analysis || null;
      setKycAnalysis(analysis);

      // Load customer files from database
      const token = localStorage.getItem('amlguard_token');
      if (token && company?.id) {
        fetch(`/api/query?resource=customer_files&filter={"customer_id":"${selectedCustomer.id}"}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
          .then(res => res.json())
          .then(data => {
            const files = Array.isArray(data) ? data : [];
            console.log('[CustomersPage] Loaded', files.length, 'customer files');
            setCustomerDocuments(files);
          })
          .catch(err => {
            console.error('[CustomersPage] Failed to load files:', err);
            setCustomerDocuments([]);
          });
      }
    } else {
      setCustomerDocuments([]);
      setKycAnalysis(null);
    }
  }, [selectedCustomer?.id, company?.id]);

  // Load compliance data (alerts, SARs, CTRs, EDD reviews)
  useEffect(() => {
    if (!selectedCustomer || !company?.id) {
      setAlerts([]);
      setSars([]);
      setCtrs([]);
      setEddReviews([]);
      setEddCustomer(null);
      return;
    }

    setComplianceLoading(true);
    const token = localStorage.getItem('amlguard_token');
    const customerName = getName(selectedCustomer);

    Promise.all([
      // Load cases from cases table (has entity information)
      fetch(`/api/query?resource=cases&filter={"company_id":"${company.id}"}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }).then(r => r.json()).catch(err => {
        console.error('[CustomersPage] Alerts fetch error', err);
        return [];
      }),

      // Load SARs from sar_drafts
      fetch(`/api/query?resource=sar_drafts&filter={"company_id":"${company.id}"}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }).then(r => r.json()).catch(err => {
        console.error('[CustomersPage] SARs fetch error', err);
        return [];
      }),

      // Load CTRs from ctr_filings
      fetch(`/api/query?resource=ctr_filings&filter={"company_id":"${company.id}"}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }).then(r => r.json()).catch(err => {
        console.error('[CustomersPage] CTRs fetch error', err);
        return [];
      }),

      // Load EDD customers and reviews
      fetch(`/api/query?resource=edd_customers&filter={"company_id":"${company.id}"}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }).then(r => r.json()).catch(err => {
        console.error('[CustomersPage] EDD customers fetch error', err);
        return [];
      })
    ]).then(([alertsData, sarsData, ctrsData, eddCustomersData]) => {
      // Filter cases by entity name
      const customerAlerts = Array.isArray(alertsData)
        ? alertsData.filter(a => {
            const entityName = a.entity || '';
            return entityName.toLowerCase().includes(customerName.toLowerCase());
          })
        : [];
      console.log('[CustomersPage] Filtered', alertsData.length, 'cases for', customerName, '→', customerAlerts.length, 'matches');
      setAlerts(customerAlerts);

      // Filter SARs by customer name (same as CTRs)
      const customerSars = Array.isArray(sarsData)
        ? sarsData.filter(s => {
            const subj = s.subject_name || '';
            return subj.toLowerCase().includes(customerName.toLowerCase());
          })
        : [];
      setSars(customerSars);

      // Filter CTRs by subject name
      const customerCtrs = Array.isArray(ctrsData)
        ? ctrsData.filter(c => {
            const subj = c.subject_name || '';
            return subj.toLowerCase().includes(customerName.toLowerCase());
          })
        : [];
      setCtrs(customerCtrs);

      // Find matching EDD customer
      const eddCustomers = Array.isArray(eddCustomersData) ? eddCustomersData : [];
      const matchingEdd = eddCustomers.find(e => {
        const name = e.customer_name || '';
        return name.toLowerCase() === customerName.toLowerCase();
      });

      if (matchingEdd) {
        setEddCustomer(matchingEdd);
        // Load EDD reviews for this customer
        fetch(`/api/query?resource=edd_reviews&filter={"customer_id":"${matchingEdd.id}"}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })
          .then(r => r.json())
          .then(reviewsData => {
            setEddReviews(Array.isArray(reviewsData) ? reviewsData : []);
          })
          .catch(err => {
            console.error('[CustomersPage] EDD reviews fetch error', err);
            setEddReviews([]);
          });
      } else {
        setEddReviews([]);
      }

      setComplianceLoading(false);
    });
  }, [selectedCustomer, company?.id]);

  const getName = (c) => {
    if (c.type === "business") return c.business_name || "—";
    return [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
  };

  const downloadDocument = async (doc) => {
    if (!selectedCustomer) return;

    try {
      const token = localStorage.getItem('amlguard_token');
      if (!token) {
        alert('Not authenticated');
        return;
      }

      console.log('[CustomersPage] Downloading file:', doc.name, 'ID:', doc.id);

      // Fetch file from Neon database
      const response = await fetch(`/api/files/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileId: doc.id }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Download failed' }));
        throw new Error(error.error || 'Download failed');
      }

      // Get file blob from response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Trigger browser download
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('[CustomersPage] Download initiated:', doc.name);
    } catch (err) {
      console.error('[CustomersPage] Download error:', err.message);
      alert('Failed to download: ' + err.message);
    }
  };

  const startEdit = (customer) => {
    setEditFormData({
      // Personal Info
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      dob: customer.dob || '',
      nationality: customer.nationality || '',
      tax_id: customer.tax_id || '',

      // Contact & Work
      job_title: customer.job_title || '',
      employer_name: customer.employer_name || '',
      annual_income: customer.annual_income || '',

      // Address
      address_line1: customer.address_line1 || '',
      address_line2: customer.address_line2 || '',
      city: customer.city || '',
      post_code: customer.post_code || '',
      country: customer.country || '',

      // Business Info
      business_name: customer.business_name || '',
      business_type: customer.business_type || '',
      trading_name: customer.trading_name || '',
      reg_number: customer.reg_number || '',
      business_tax_id: customer.business_tax_id || '',
      incorporation_country: customer.incorporation_country || '',
      website: customer.website || '',

      // Risk
      risk_score: customer.risk_score ?? '',
      risk_rating: customer.risk_rating || 'Low'
    });
    setChangeReason('');
    setUploadedFiles([]);
    setIsEditMode(true);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const saveChanges = async () => {
    if (!changeReason.trim()) {
      alert('Please provide a reason for the change');
      return;
    }

    if (!selectedCustomer?.id) return;

    setIsSaving(true);
    try {
      const token = localStorage.getItem('amlguard_token');
      const documents = [];

      // Upload files if any were provided
      if (uploadedFiles.length > 0) {
        console.log('[CustomersPage] Uploading', uploadedFiles.length, 'files...');
        for (const file of uploadedFiles) {
          try {
            // Read file as base64
            const fileData = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            // Upload to Neon via API
            const uploadRes = await fetch('/api/files/upload', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                filename: file.name,
                filetype: file.type || 'application/octet-stream',
                filedata: fileData,
                companyId: selectedCustomer.company_id,
                customerId: selectedCustomer.id,
              }),
            });

            if (!uploadRes.ok) {
              const error = await uploadRes.json().catch(() => ({ error: 'Upload failed' }));
              console.warn('[CustomersPage] Failed to upload file:', file.name, error.error);
              continue;
            }

            const uploadData = await uploadRes.json();
            documents.push({
              id: uploadData.file.id,
              file_name: file.name,
              file_size: file.size,
              file_type: file.type,
              file_path: storagePath,
              document_type: 'supporting_document'
            });

            console.log('[CustomersPage] File uploaded:', file.name);
          } catch (fileErr) {
            console.warn('[CustomersPage] Failed to upload file:', file.name, fileErr.message);
            // Continue with other files even if one fails
          }
        }
      }

      // Prepare customer data - include all fields
      const updatePayload = {
        id: selectedCustomer.id,
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        email: editFormData.email,
        phone: editFormData.phone,
        dob: editFormData.dob,
        nationality: editFormData.nationality,
        tax_id: editFormData.tax_id,
        job_title: editFormData.job_title,
        employer_name: editFormData.employer_name,
        annual_income: editFormData.annual_income,
        address_line1: editFormData.address_line1,
        address_line2: editFormData.address_line2,
        city: editFormData.city,
        post_code: editFormData.post_code,
        country: editFormData.country,
        business_name: editFormData.business_name,
        business_type: editFormData.business_type,
        trading_name: editFormData.trading_name,
        reg_number: editFormData.reg_number,
        business_tax_id: editFormData.business_tax_id,
        incorporation_country: editFormData.incorporation_country,
        website: editFormData.website,
        risk_score: editFormData.risk_score ? parseInt(editFormData.risk_score) : null,
        risk_rating: editFormData.risk_rating,
        change_reason: changeReason,
        changed_by: currentUser?.id,
        documents
      };

      console.log('[CustomersPage] Sending update payload:', updatePayload);

      // Save customer changes
      const response = await fetch('/api/customers/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatePayload)
      });

      console.log('[CustomersPage] API response status:', response.status);
      const responseText = await response.text();
      console.log('[CustomersPage] API response:', responseText);

      if (!response.ok) {
        let error;
        try {
          error = JSON.parse(responseText);
        } catch {
          error = { error: responseText || 'Failed to save changes' };
        }
        throw new Error(error.error || error.message || 'Failed to save changes');
      }

      let updatedCustomer;
      try {
        updatedCustomer = JSON.parse(responseText);
      } catch {
        throw new Error('Invalid response from server');
      }

      console.log('[CustomersPage] Updated customer:', updatedCustomer);

      // Update local customer list and selected customer
      setCustomers(prev =>
        prev.map(c => c.id === selectedCustomer.id ? updatedCustomer : c)
      );
      setSelectedCustomer(updatedCustomer);
      setIsEditMode(false);
      setChangeReason('');
      setUploadedFiles([]);

      // Show save success message
      setSaveMessage('Customer updated successfully' + (documents.length > 0 ? ` with ${documents.length} document(s)` : ''));
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('[CustomersPage] Save error:', error);
      setSaveMessage(`Error saving changes: ${error.message}`);
      setTimeout(() => setSaveMessage(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: C.textDim }}>
        Loading customers...
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: C.textDim }}>
        No approved customers yet. Customers will appear here after they complete onboarding.
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ color: C.text, fontSize: 24, fontWeight: 700, margin: 0 }}>
          {t('customers.title', { count: customers.length })}
        </h1>
        <div style={{ color: C.textDim, fontSize: 12, marginTop: 4 }}>
          {t('customers.subtitle')}
        </div>
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
                {[t('customers.tableHeaders.name'), t('customers.tableHeaders.type'), t('customers.tableHeaders.country'), t('customers.tableHeaders.riskScore'), t('customers.tableHeaders.riskRating'), t('customers.tableHeaders.created')].map(h => (
                  <th key={h} style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    color: C.textDim,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.map((c, i) => (
                <tr
                  key={c.id}
                  onClick={() => setSelectedCustomer(c)}
                  style={{
                    borderBottom: `1px solid ${C.border}`,
                    background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'background 0.1s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = C.surfaceAlt}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.1)'}
                >
                  <td style={{ padding: '12px 16px', color: C.text, fontWeight: 500 }}>
                    {getName(c)}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.textMid, fontSize: 12, textTransform: 'capitalize' }}>
                    {c.type || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.textMid, fontSize: 12 }}>
                    {c.country || c.incorporation_country || c.nationality || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.text, fontWeight: 600 }}>
                    {c.risk_score ?? '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.textMid, fontSize: 12, textTransform: 'capitalize' }}>
                    {c.risk_rating || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: C.textDim, fontSize: 11 }}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedCustomer && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: C.surface,
            borderRadius: 12,
            padding: '24px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: `1px solid ${C.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ color: C.text, fontSize: 18, fontWeight: 700, margin: 0 }}>
                  {getName(selectedCustomer)}
                  {isEditMode && <span style={{ color: C.warn, fontSize: 12, fontWeight: 400, marginLeft: 8 }}>{t('customers.editing')}</span>}
                </h2>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {!isEditMode && (
                  <button
                    onClick={() => startEdit(selectedCustomer)}
                    style={{
                      background: C.accent,
                      border: 'none',
                      color: '#000',
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: '6px 12px',
                      borderRadius: 6,
                      fontWeight: 600
                    }}
                  >
                    {t('customers.edit')}
                  </button>
                )}
                {isEditMode && (
                  <>
                    <button
                      onClick={() => setIsEditMode(false)}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${C.border}`,
                        color: C.textMid,
                        cursor: 'pointer',
                        fontSize: 12,
                        padding: '6px 12px',
                        borderRadius: 6
                      }}
                    >
                      {t('customers.cancel')}
                    </button>
                    <button
                      onClick={saveChanges}
                      disabled={isSaving}
                      style={{
                        background: C.success,
                        border: 'none',
                        color: '#000',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        fontSize: 12,
                        padding: '6px 12px',
                        borderRadius: 6,
                        fontWeight: 600,
                        opacity: isSaving ? 0.6 : 1
                      }}
                    >
                      {isSaving ? t('customers.saving') : t('customers.save')}
                    </button>
                  </>
                )}
                <button
                  onClick={() => setSelectedCustomer(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: C.textDim,
                    cursor: 'pointer',
                    fontSize: 24,
                    padding: 0
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Tab Navigation */}
            {!isEditMode && (
              <div style={{
                display: 'flex',
                gap: '4px',
                marginBottom: '20px',
                borderBottom: `1px solid ${C.border}`,
                overflowX: 'auto'
              }}>
                {['profile', 'alerts', 'sars', 'ctrs', 'edd', 'documents', 'kyc-analysis'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setSelectedTab(tab)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: selectedTab === tab ? C.accent : C.textMid,
                      borderBottom: selectedTab === tab ? `2px solid ${C.accent}` : '2px solid transparent',
                      cursor: 'pointer',
                      fontSize: 13,
                      padding: '12px 16px',
                      fontWeight: selectedTab === tab ? 600 : 400,
                      textTransform: 'capitalize',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {tab === 'profile' && 'Profile'}
                    {tab === 'alerts' && `Alerts (${alerts?.length || 0})`}
                    {tab === 'sars' && `SARs (${sars?.length || 0})`}
                    {tab === 'ctrs' && `CTRs (${ctrs?.length || 0})`}
                    {tab === 'edd' && 'EDD'}
                    {tab === 'documents' && `Documents (${customerDocuments?.length || 0})`}
                    {tab === 'kyc-analysis' && 'KYC Analysis'}
                  </button>
                ))}
              </div>
            )}

            {!isEditMode ? (
              // VIEW MODE - TAB CONTENT
              <div>
                {selectedTab === 'profile' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto', maxHeight: '600px', paddingRight: '12px' }}>
                    {/* PERSONAL INFORMATION */}
                    {selectedCustomer.type === 'individual' && (
                      <div>
                        <div style={{ color: C.textMid, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                          PERSONAL INFORMATION
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Full Name</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.full_name || [selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ') || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Date of Birth</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.dob || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Nationality</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.nationality || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Email</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.email || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Phone</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.phone || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Tax ID</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.tax_id || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Job Title</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.job_title || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Employer</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.employer_name || '—'}</div>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Purpose of Account</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.purpose_of_account || '—'}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ADDRESS */}
                    {(selectedCustomer.address_line1 || selectedCustomer.city || selectedCustomer.country) && (
                      <div>
                        <div style={{ color: C.textMid, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                          ADDRESS
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Line 1</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.address_line1 || '—'}</div>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Line 2</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.address_line2 || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>City</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.city || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Post Code</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.post_code || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Country</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.country || '—'}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* BUSINESS INFORMATION */}
                    {selectedCustomer.type === 'business' && (
                      <div>
                        <div style={{ color: C.textMid, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                          BUSINESS
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Business Name</div>
                            <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{selectedCustomer.business_name || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Reg Number</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.reg_number || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Type</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.business_type || '—'}</div>
                          </div>
                          <div>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Country</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.incorporation_country || '—'}</div>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Website</div>
                            <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.website || '—'}</div>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Directors/UBOs</div>
                            <div style={{ color: C.text, fontSize: 13 }}>
                              {selectedCustomer.ubos ? (
                                typeof selectedCustomer.ubos === 'string'
                                  ? JSON.parse(selectedCustomer.ubos).map((u, i) => (
                                    <div key={i}>{u.name} ({u.role} · {u.ownership}%)</div>
                                  ))
                                  : Array.isArray(selectedCustomer.ubos) && selectedCustomer.ubos.map((u, i) => (
                                    <div key={i}>{u.name || u} ({u.role || 'Director'} · {u.ownership || '—'}%)</div>
                                  ))
                              ) : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* COMPLIANCE INFORMATION */}
                    <div>
                      <div style={{ color: C.textMid, fontSize: 11, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                        COMPLIANCE
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Source of Funds</div>
                          <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.source_of_funds || '—'}</div>
                        </div>
                        <div>
                          <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Annual Income</div>
                          <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.annual_income || '—'}</div>
                        </div>
                        <div>
                          <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>PEP Declaration</div>
                          <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.pep_declaration === true ? 'Yes' : selectedCustomer.pep_declaration === false ? 'No' : '—'}</div>
                        </div>
                        <div>
                          <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Sanctions Declaration</div>
                          <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.sanctions_declaration === true ? 'Yes' : selectedCustomer.sanctions_declaration === false ? 'No' : '—'}</div>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Account Number</div>
                          <div style={{ color: C.accent, fontSize: 13, fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{selectedCustomer.account_number || '—'}</div>
                        </div>
                        <div>
                          <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Risk Score</div>
                          <div style={{ color: C.text, fontSize: 13 }}>{selectedCustomer.risk_score ?? '—'}</div>
                        </div>
                        <div>
                          <div style={{ color: C.textDim, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Risk Rating</div>
                          <div style={{ color: C.text, fontSize: 13, textTransform: 'capitalize' }}>{selectedCustomer.risk_rating || '—'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ALERTS/CASES TAB */}
                {selectedTab === 'alerts' && (
                  <div>
                    {complianceLoading ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>Loading cases...</div>
                    ) : alerts.length === 0 ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>No cases found for this customer</div>
                    ) : (
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {alerts.map((alert, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: '12px',
                              border: `1px solid ${C.border}`,
                              borderRadius: 6,
                              background: (alert.risk_rating || '').toUpperCase() === 'HIGH' ? `${C.danger}15` : `${C.accent}10`
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                              <div style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>
                                {alert.case_type || 'Case'}
                              </div>
                              <div style={{
                                color: (alert.risk_rating || '').toUpperCase() === 'HIGH' ? C.danger : C.accent,
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: 'uppercase'
                              }}>
                                {alert.risk_rating || '—'}
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: 11, marginBottom: 8 }}>
                              <div>
                                <span style={{ color: C.textDim, fontSize: 10 }}>Status: </span>
                                <span style={{ color: C.text, textTransform: 'capitalize' }}>{alert.status || '—'}</span>
                              </div>
                              <div>
                                <span style={{ color: C.textDim, fontSize: 10 }}>Disposition: </span>
                                <span style={{
                                  color: (alert.disposition || '').toLowerCase() === 'escalated' ? C.danger : C.success,
                                  textTransform: 'capitalize',
                                  fontWeight: 500
                                }}>
                                  {alert.disposition || '—'}
                                </span>
                              </div>
                              <div>
                                <span style={{ color: C.textDim, fontSize: 10 }}>Date: </span>
                                <span style={{ color: C.text }}>
                                  {alert.created_at ? new Date(alert.created_at).toLocaleDateString() : '—'}
                                </span>
                              </div>
                            </div>
                            {alert.description && (
                              <div style={{ color: C.textMid, fontSize: 11, marginTop: 8, lineHeight: 1.4 }}>
                                {alert.description}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* SARs TAB */}
                {selectedTab === 'sars' && (
                  <div>
                    {complianceLoading ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>Loading SARs...</div>
                    ) : sars.length === 0 ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>No SARs found</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt }}>
                              <th style={{ padding: '10px 8px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Status</th>
                              <th style={{ padding: '10px 8px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Filing Deadline</th>
                              <th style={{ padding: '10px 8px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sars.map((sar, idx) => (
                              <tr key={idx} style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td style={{ padding: '10px 8px', color: C.text, fontWeight: 500, textTransform: 'capitalize' }}>
                                  {sar.status || '—'}
                                </td>
                                <td style={{ padding: '10px 8px', color: C.textMid }}>
                                  {sar.filing_deadline ? new Date(sar.filing_deadline).toLocaleDateString() : '—'}
                                </td>
                                <td style={{ padding: '10px 8px', color: C.textDim, fontSize: 10 }}>
                                  {sar.created_at ? new Date(sar.created_at).toLocaleDateString() : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* CTRs TAB */}
                {selectedTab === 'ctrs' && (
                  <div>
                    {complianceLoading ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>Loading CTRs...</div>
                    ) : ctrs.length === 0 ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>No CTRs found for this customer</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt }}>
                              <th style={{ padding: '10px 8px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Subject</th>
                              <th style={{ padding: '10px 8px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Filing Date</th>
                              <th style={{ padding: '10px 8px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ctrs.map((ctr, idx) => (
                              <tr key={idx} style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td style={{ padding: '10px 8px', color: C.text }}>
                                  {ctr.subject_name || '—'}
                                </td>
                                <td style={{ padding: '10px 8px', color: C.textMid, fontSize: 11 }}>
                                  {ctr.filing_date ? new Date(ctr.filing_date).toLocaleDateString() : '—'}
                                </td>
                                <td style={{ padding: '10px 8px', color: C.textDim, fontSize: 10, textTransform: 'capitalize' }}>
                                  {ctr.status || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* EDD TAB */}
                {selectedTab === 'edd' && (
                  <div>
                    {complianceLoading ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>Loading EDD data...</div>
                    ) : !eddCustomer ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>No EDD profile found for this customer</div>
                    ) : (
                      <div style={{ display: 'grid', gap: '16px' }}>
                        <div>
                          <h4 style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, margin: '0 0 8px 0' }}>
                            EDD Profile
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                              <div style={{ color: C.textDim, fontSize: 9, marginBottom: 2 }}>Customer Type</div>
                              <div style={{ color: C.text, fontSize: 13 }}>{eddCustomer.customer_type || '—'}</div>
                            </div>
                            <div>
                              <div style={{ color: C.textDim, fontSize: 9, marginBottom: 2 }}>Risk Level</div>
                              <div style={{ color: C.text, fontSize: 13, textTransform: 'capitalize' }}>{eddCustomer.risk_level || '—'}</div>
                            </div>
                          </div>
                        </div>

                        {eddReviews.length > 0 && (
                          <div>
                            <h4 style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, margin: '0 0 8px 0' }}>
                              Reviews ({eddReviews.length})
                            </h4>
                            <div style={{ display: 'grid', gap: '8px' }}>
                              {eddReviews.map((review, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    padding: '10px',
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 4,
                                    background: `${C.accent}08`
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 4 }}>
                                    <div style={{ color: C.text, fontWeight: 500, fontSize: 11, textTransform: 'capitalize' }}>
                                      Status: {review.status || '—'}
                                    </div>
                                    <div style={{ color: C.textDim, fontSize: 9 }}>
                                      {review.created_at ? new Date(review.created_at).toLocaleDateString() : '—'}
                                    </div>
                                  </div>
                                  {review.reviewer_notes && (
                                    <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.4 }}>
                                      {review.reviewer_notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* DOCUMENTS TAB */}
                {selectedTab === 'documents' && (
                  <div>
                    {customerDocuments.length === 0 ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>No documents uploaded for this customer</div>
                    ) : (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {customerDocuments.map((doc, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: '12px 16px',
                              border: `1px solid ${C.border}`,
                              borderRadius: 4,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              background: C.surfaceAlt
                            }}
                          >
                            <div>
                              <div style={{ color: C.text, fontSize: 12, fontWeight: 500 }}>{doc.file_name || '—'}</div>
                              <div style={{ color: C.textDim, fontSize: 10, marginTop: 2 }}>
                                Uploaded {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '—'}
                              </div>
                            </div>
                            <button
                              onClick={() => downloadDocument(doc)}
                              style={{
                                background: C.accent,
                                border: 'none',
                                color: C.bg,
                                padding: '6px 12px',
                                borderRadius: 3,
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: 600
                              }}
                            >
                              Download
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* KYC ANALYSIS TAB */}
                {selectedTab === 'kyc-analysis' && (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {!kycAnalysis ? (
                      <div style={{ color: C.textDim, fontSize: 12, padding: '16px' }}>No KYC analysis available for this customer</div>
                    ) : (
                      <>
                        {/* Overall Status */}
                        <div>
                          <h4 style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, margin: '0 0 8px 0' }}>Overall Status</h4>
                          <div style={{
                            padding: '12px',
                            borderRadius: 4,
                            background: kycAnalysis.overall === 'APPROVED' ? `${C.success}20` : kycAnalysis.overall === 'REJECTED' ? `${C.danger}20` : `${C.warn}20`,
                            border: `1px solid ${kycAnalysis.overall === 'APPROVED' ? C.success : kycAnalysis.overall === 'REJECTED' ? C.danger : C.warn}`
                          }}>
                            <div style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{kycAnalysis.overall || '—'}</div>
                            <div style={{ color: C.textMid, fontSize: 11, marginTop: 4 }}>Confidence: {kycAnalysis.confidence || '—'}%</div>
                          </div>
                        </div>

                        {/* Summary */}
                        {kycAnalysis.summary && (
                          <div>
                            <h4 style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, margin: '0 0 8px 0' }}>Summary</h4>
                            <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.5, padding: '12px', background: C.surfaceAlt, borderRadius: 4 }}>
                              {kycAnalysis.summary}
                            </div>
                          </div>
                        )}

                        {/* Red Flags */}
                        {kycAnalysis.red_flags && kycAnalysis.red_flags.length > 0 && (
                          <div>
                            <h4 style={{ color: C.danger, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, margin: '0 0 8px 0' }}>Red Flags ({kycAnalysis.red_flags.length})</h4>
                            <div style={{ display: 'grid', gap: '6px' }}>
                              {kycAnalysis.red_flags.map((flag, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    padding: '10px',
                                    background: `${C.danger}08`,
                                    border: `1px solid ${C.danger}40`,
                                    borderRadius: 4,
                                    color: C.text,
                                    fontSize: 11
                                  }}
                                >
                                  {flag}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recommendations */}
                        {kycAnalysis.recommendations && kycAnalysis.recommendations.length > 0 && (
                          <div>
                            <h4 style={{ color: C.accent, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, margin: '0 0 8px 0' }}>Recommendations ({kycAnalysis.recommendations.length})</h4>
                            <div style={{ display: 'grid', gap: '6px' }}>
                              {kycAnalysis.recommendations.map((rec, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    padding: '10px',
                                    background: `${C.accent}08`,
                                    border: `1px solid ${C.accent}40`,
                                    borderRadius: 4,
                                    color: C.text,
                                    fontSize: 11
                                  }}
                                >
                                  {rec}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // EDIT MODE - Multi-section form
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '16px' }}>
                {/* Personal Information Section */}
                {selectedCustomer.type === 'individual' && (
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
                    <h3 style={{ color: C.text, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12, margin: 0 }}>{t('customers.sections.personal')}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.firstName')}</label>
                        <input type="text" value={editFormData.first_name} onChange={(e) => setEditFormData(prev => ({ ...prev, first_name: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.lastName')}</label>
                        <input type="text" value={editFormData.last_name} onChange={(e) => setEditFormData(prev => ({ ...prev, last_name: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.dateOfBirth')}</label>
                        <input type="date" value={editFormData.dob} onChange={(e) => setEditFormData(prev => ({ ...prev, dob: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.nationality')}</label>
                        <input type="text" value={editFormData.nationality} onChange={(e) => setEditFormData(prev => ({ ...prev, nationality: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.taxId')}</label>
                        <input type="text" value={editFormData.tax_id} onChange={(e) => setEditFormData(prev => ({ ...prev, tax_id: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Information Section */}
                {selectedCustomer.type === 'individual' && (
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
                    <h3 style={{ color: C.text, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12, margin: 0 }}>{t('customers.sections.contact')}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.email')}</label>
                        <input type="email" value={editFormData.email} onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.phone')}</label>
                        <input type="tel" value={editFormData.phone} onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Work Information Section */}
                {selectedCustomer.type === 'individual' && (
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
                    <h3 style={{ color: C.text, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12, margin: 0 }}>{t('customers.sections.employment')}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.jobTitle')}</label>
                        <input type="text" value={editFormData.job_title} onChange={(e) => setEditFormData(prev => ({ ...prev, job_title: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.employer')}</label>
                        <input type="text" value={editFormData.employer_name} onChange={(e) => setEditFormData(prev => ({ ...prev, employer_name: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Annual Income</label>
                        <input type="text" value={editFormData.annual_income} onChange={(e) => setEditFormData(prev => ({ ...prev, annual_income: e.target.value }))} placeholder="e.g., $95,000" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Address Section */}
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
                  <h3 style={{ color: C.text, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12, margin: 0 }}>{t('customers.sections.address')}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.addressLine1')}</label>
                      <input type="text" value={editFormData.address_line1} onChange={(e) => setEditFormData(prev => ({ ...prev, address_line1: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.addressLine2')}</label>
                      <input type="text" value={editFormData.address_line2} onChange={(e) => setEditFormData(prev => ({ ...prev, address_line2: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                    <div>
                      <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.city')}</label>
                      <input type="text" value={editFormData.city} onChange={(e) => setEditFormData(prev => ({ ...prev, city: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                    <div>
                      <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.postalCode')}</label>
                      <input type="text" value={editFormData.post_code} onChange={(e) => setEditFormData(prev => ({ ...prev, post_code: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Country</label>
                      <input type="text" value={editFormData.country} onChange={(e) => setEditFormData(prev => ({ ...prev, country: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                  </div>
                </div>

                {/* Business Information Section */}
                {selectedCustomer.type === 'business' && (
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
                    <h3 style={{ color: C.text, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12, margin: 0 }}>Business Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Business Name</label>
                        <input type="text" value={editFormData.business_name} onChange={(e) => setEditFormData(prev => ({ ...prev, business_name: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Trading Name</label>
                        <input type="text" value={editFormData.trading_name} onChange={(e) => setEditFormData(prev => ({ ...prev, trading_name: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Business Type</label>
                        <input type="text" value={editFormData.business_type} onChange={(e) => setEditFormData(prev => ({ ...prev, business_type: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Registration Number</label>
                        <input type="text" value={editFormData.reg_number} onChange={(e) => setEditFormData(prev => ({ ...prev, reg_number: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('customers.fields.taxId')}</label>
                        <input type="text" value={editFormData.business_tax_id} onChange={(e) => setEditFormData(prev => ({ ...prev, business_tax_id: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Incorporation Country</label>
                        <input type="text" value={editFormData.incorporation_country} onChange={(e) => setEditFormData(prev => ({ ...prev, incorporation_country: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Website</label>
                        <input type="url" value={editFormData.website} onChange={(e) => setEditFormData(prev => ({ ...prev, website: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Risk Information Section */}
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
                  <h3 style={{ color: C.text, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', marginBottom: 12, margin: 0 }}>Risk Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Risk Score</label>
                      <input type="number" min="0" max="100" value={editFormData.risk_score} onChange={(e) => setEditFormData(prev => ({ ...prev, risk_score: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }} />
                    </div>
                    <div>
                      <label style={{ color: C.textDim, fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 4 }}>Risk Rating</label>
                      <select value={editFormData.risk_rating} onChange={(e) => setEditFormData(prev => ({ ...prev, risk_rating: e.target.value }))} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.text, padding: '8px 10px', borderRadius: 4, width: '100%', fontSize: 12, fontFamily: 'inherit' }}>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isEditMode && (
              <div style={{ paddingTop: '16px', borderTop: `1px solid ${C.border}` }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Reason for Change *
                  </label>
                  <textarea
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    placeholder="Explain why you are making these changes..."
                    style={{
                      background: C.surfaceAlt,
                      border: `1px solid ${C.border}`,
                      color: C.text,
                      padding: '10px 12px',
                      borderRadius: 6,
                      width: '100%',
                      fontSize: 13,
                      fontFamily: 'inherit',
                      minHeight: '80px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    Upload Supporting Documents
                  </label>
                  <label style={{
                    display: 'block',
                    padding: '20px',
                    border: `2px dashed ${C.border}`,
                    borderRadius: 6,
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: `${C.border}20`,
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                    />
                    <div style={{ color: C.textMid, fontSize: 12 }}>
                      Click to upload or drag and drop
                    </div>
                    <div style={{ color: C.textDim, fontSize: 10, marginTop: 4 }}>
                      PDF, DOC, DOCX, JPG, PNG, TXT
                    </div>
                  </label>

                  {uploadedFiles.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ color: C.textDim, fontSize: 10, marginBottom: 6 }}>
                        {uploadedFiles.length} file(s) selected:
                      </div>
                      {uploadedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 10px',
                            background: `${C.accent}20`,
                            border: `1px solid ${C.accent}40`,
                            borderRadius: 4,
                            marginBottom: 6,
                            fontSize: 12
                          }}
                        >
                          <span style={{ color: C.text }}>{file.name}</span>
                          <button
                            onClick={() => removeFile(idx)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: C.danger,
                              cursor: 'pointer',
                              fontSize: 16,
                              padding: 0
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isEditMode && selectedTab === 'profile' && (
              <>
                {selectedCustomer.documents && selectedCustomer.documents.length > 0 && (
                  <div style={{ paddingTop: '20px', borderTop: `1px solid ${C.border}` }}>
                    <div style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, marginBottom: 12 }}>
                      📄 Documents ({selectedCustomer.documents.length})
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {selectedCustomer.documents.map((doc, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '10px 12px',
                            background: `${C.accent}15`,
                            border: `1px solid ${C.accent}40`,
                            borderRadius: 6,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: 12
                          }}
                        >
                          <div>
                            <div style={{ color: C.text, fontWeight: 500 }}>{doc.file_name}</div>
                            <div style={{ color: C.textDim, fontSize: 10, marginTop: 2 }}>
                              {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : '—'} • {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}
                            </div>
                          </div>
                          {doc.file_path && (
                            <a
                              href={doc.file_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: C.accent,
                                textDecoration: 'none',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                              title="Download document"
                            >
                              ↓
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCustomer.notes && (
                  <div style={{ paddingTop: '20px', borderTop: `1px solid ${C.border}` }}>
                    <div style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>Notes</div>
                    <div style={{ color: C.textMid, fontSize: 13, lineHeight: 1.6 }}>
                      {selectedCustomer.notes}
                    </div>
                  </div>
                )}

                <div style={{ paddingTop: '20px', borderTop: `1px solid ${C.border}` }}>
                  <div style={{ color: C.textDim, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, marginBottom: 12 }}>
                    Transactions ({transactions.length})
                  </div>
                  {transactionsLoading ? (
                    <div style={{ color: C.textDim, fontSize: 12 }}>Loading transactions...</div>
                  ) : transactions.length === 0 ? (
                    <div style={{ color: C.textDim, fontSize: 12 }}>No transactions found for this customer</div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                            <th style={{ padding: '8px 4px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Date</th>
                            <th style={{ padding: '8px 4px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Amount</th>
                            <th style={{ padding: '8px 4px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Type</th>
                            <th style={{ padding: '8px 4px', textAlign: 'left', color: C.textDim, fontWeight: 600 }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.slice(0, 10).map((tx, idx) => (
                            <tr key={idx} style={{ borderBottom: `1px solid ${C.border}` }}>
                              <td style={{ padding: '8px 4px', color: C.text }}>
                                {tx.date ? new Date(tx.date).toLocaleDateString() : tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '—'}
                              </td>
                              <td style={{ padding: '8px 4px', color: C.text, fontWeight: 500 }}>
                                ${tx.amount ? parseFloat(tx.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                              </td>
                              <td style={{ padding: '8px 4px', color: C.textMid, fontSize: 10, textTransform: 'uppercase' }}>
                                {tx.type || '—'}
                              </td>
                              <td style={{ padding: '8px 4px', color: C.textMid, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.description}>
                                {tx.description || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {transactions.length > 10 && (
                        <div style={{ marginTop: '8px', color: C.textDim, fontSize: 11 }}>
                          Showing 10 of {transactions.length} transactions
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Save notification toast */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: saveMessage.includes('Error') ? C.danger : C.success,
          color: '#000',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {saveMessage}
        </div>
      )}
    </div>
  );
}
